// DOM input for the match: pointer lock, mouse look, keybinds -> Buttons, weapon slots, sub-tick
// fire. Mouse deltas are accumulated by the event handlers and turned into view angles once per
// frame (`applyMouse`); a trigger pull flushes them first so the recorded angles are exact.

import type { Action } from "../settings/schema";
import { TRACE_CODE } from "../settings/keybinds";
import { settings } from "../settings/store";
import type { InputCmdData } from "../wasm/types";
import {
  DEG2RAD,
  clampPitch,
  quantizePitch,
  quantizeYaw,
  wrapYaw,
} from "./angles";
import { Keymap, buttonMask, mouseBinding, wheelBinding } from "./buttons";
import type { ButtonOverrides, EdgeAction } from "./buttons";
import { degreesPerCount } from "./cameraMath";
import { FireQueue, resolveFire } from "./fire";
import { HeldBindings } from "./held";
import { SlotSelector } from "./slots";
import { WheelNotches } from "./wheel";
import { WHEEL_JUMP_MIN_GAP_MS, WheelJump } from "./wheelJump";

export interface InputHooks {
  /** Pointer lock was lost (Escape, alt-tab) or Escape was pressed while unlocked: open the pause menu. */
  onPauseRequested(): void;
  onChat(teamOnly: boolean): void;
  onScoreboard(show: boolean): void;
  /** The lobby key (custom lobbies): opens or closes the lobby panel. */
  onLobby(): void;
  /** F8 (`TRACE_CODE`, never a keybind): save the movement trace of the last ten seconds. */
  onTrace(): void;
  /** The fire button went down (also while dead: cycles the watched player). */
  onFire(): void;
  chatIsOpen(): boolean;
  onLockChange(locked: boolean): void;
}

/** Mouse event deltas beyond this are browser glitches (lock entry spikes), not real movement. */
const MAX_EVENT_DELTA = 1000;

function isTextTarget(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false;
  return (
    t.isContentEditable ||
    t.tagName === "INPUT" ||
    t.tagName === "TEXTAREA" ||
    t.tagName === "SELECT"
  );
}

export class InputController {
  /** Live view angles in radians (include recoil). yaw in [0, 2pi), positive turns left. */
  yaw = 0;
  pitch = 0;
  /** Mouse movement applied by the last `applyMouse`, for recoil compensation and viewmodel sway (degrees). */
  deltaPitchUpDeg = 0;
  deltaYawRightDeg = 0;

  readonly slots = new SlotSelector();
  readonly fires = new FireQueue();

  /** The match accepts input (playing and not paused). */
  enabled = false;

  private readonly keys = new HeldBindings();
  private keymap: Keymap;
  private keybindsRef = settings.value.keybinds;
  private readonly wheel = new WheelNotches();
  /** "Jump on mouse wheel": notch detection (no minimum gap) and the notches waiting for a sampled tick. */
  private readonly jumpNotches = new WheelNotches(WHEEL_JUMP_MIN_GAP_MS);
  private readonly wheelJump = new WheelJump();
  private pendingDx = 0;
  private pendingDy = 0;
  private adsFrac = 0;
  private adsFovMult = 1;
  private scoped = false;
  private adsToggle = false;
  private crouchToggle = false;
  private prevButtons = 0;
  private lockedState = false;
  private wasLocked = false;
  private attached = false;
  private readonly overrides: ButtonOverrides = {};

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly hooks: InputHooks,
  ) {
    this.keymap = new Keymap(settings.value.keybinds);
  }

  get locked(): boolean {
    return this.lockedState;
  }

  attach(): void {
    if (this.attached) return;
    this.attached = true;
    document.addEventListener("keydown", this.onKeyDown);
    document.addEventListener("keyup", this.onKeyUp);
    document.addEventListener("mousedown", this.onMouseDown);
    document.addEventListener("mouseup", this.onMouseUp);
    document.addEventListener("wheel", this.onWheel, { passive: false });
    document.addEventListener("contextmenu", this.onContextMenu);
    document.addEventListener("pointerlockchange", this.onLockChange);
    window.addEventListener("blur", this.onBlur);
    document.addEventListener("visibilitychange", this.onBlur);
    // `mousemove`, not `pointerrawupdate`: Chromium dispatches a raw update per sensor report
    // as a top-priority input task, and a 1-8 kHz mouse then starves the network tasks (snapshots
    // and WebTransport write completions) for seconds (docs/NETCODE.md "Main-thread scheduling").
    // `mousemove` is coalesced once per frame with summed movement and runs before the frame's
    // rAF, so the look delta is just as fresh.
    window.addEventListener("mousemove", this.onMove);
    this.onLockChange();
  }

  detach(): void {
    if (!this.attached) return;
    this.attached = false;
    document.removeEventListener("keydown", this.onKeyDown);
    document.removeEventListener("keyup", this.onKeyUp);
    document.removeEventListener("mousedown", this.onMouseDown);
    document.removeEventListener("mouseup", this.onMouseUp);
    document.removeEventListener("wheel", this.onWheel);
    document.removeEventListener("contextmenu", this.onContextMenu);
    document.removeEventListener("pointerlockchange", this.onLockChange);
    window.removeEventListener("blur", this.onBlur);
    document.removeEventListener("visibilitychange", this.onBlur);
    window.removeEventListener("mousemove", this.onMove);
    this.releaseAll();
    if (document.pointerLockElement === this.canvas) document.exitPointerLock();
    this.lockedState = false;
    this.wasLocked = false;
  }

  // ---- Pointer lock ----

  /**
   * Captures the mouse (needs a user gesture; a refused request leaves the "click to play" hint).
   * With raw input on, a browser without `unadjustedMovement` support gets the plain request.
   */
  requestLock(): void {
    if (document.pointerLockElement === this.canvas) return;
    const raw = settings.value.controls.rawInput;
    const request = this.canvas.requestPointerLock as (options?: {
      unadjustedMovement: boolean;
    }) => unknown;
    const attempt = (
      options: { unadjustedMovement: boolean } | undefined,
      fallbackToPlain: boolean,
    ): void => {
      const retry = (): void => {
        if (fallbackToPlain && document.pointerLockElement !== this.canvas)
          attempt(undefined, false);
      };
      try {
        const result = request.call(this.canvas, options);
        if (result instanceof Promise) result.catch(retry);
      } catch {
        retry();
      }
    };
    attempt(raw ? { unadjustedMovement: true } : undefined, raw);
  }

  releaseLock(): void {
    if (document.pointerLockElement === this.canvas) document.exitPointerLock();
  }

  private readonly onLockChange = (): void => {
    const locked = document.pointerLockElement === this.canvas;
    this.lockedState = locked;
    if (locked) {
      this.wasLocked = true;
    } else {
      this.releaseAll();
      if (this.wasLocked) {
        this.wasLocked = false;
        if (!this.hooks.chatIsOpen()) this.hooks.onPauseRequested();
      }
    }
    this.hooks.onLockChange(locked);
  };

  // ---- Events ----

  private readonly onMove = (e: Event): void => {
    if (!this.lockedState || !this.enabled) return;
    const m = e as MouseEvent;
    const dx = m.movementX;
    const dy = m.movementY;
    if (Math.abs(dx) > MAX_EVENT_DELTA || Math.abs(dy) > MAX_EVENT_DELTA)
      return;
    this.pendingDx += dx;
    this.pendingDy += dy;
  };

  private readonly onKeyDown = (e: KeyboardEvent): void => {
    if (e.isComposing) return;
    if (e.code === "Escape") {
      // While locked the browser consumes Escape (pointerlockchange opens the pause menu).
      if (!this.lockedState && this.enabled && !this.hooks.chatIsOpen())
        this.hooks.onPauseRequested();
      return;
    }
    if (e.code === TRACE_CODE) {
      // Works locked or paused; the key is reserved, so no binding can collide with it.
      e.preventDefault();
      if (!e.repeat && !this.hooks.chatIsOpen() && !isTextTarget(e.target))
        this.hooks.onTrace();
      return;
    }
    if (!this.lockedState || !this.enabled) return;
    if (this.hooks.chatIsOpen() || isTextTarget(e.target)) return;
    this.syncKeymap();
    const actions = this.keymap.actions(e.code);
    if (actions.length === 0) return;
    e.preventDefault();
    if (e.repeat) return;
    this.press(e.code, actions, true);
  };

  private readonly onKeyUp = (e: KeyboardEvent): void => {
    if (!this.keys.down.has(e.code)) return;
    this.release(e.code);
    if (this.lockedState && !this.hooks.chatIsOpen()) e.preventDefault();
  };

  private readonly onMouseDown = (e: MouseEvent): void => {
    if (!this.lockedState) {
      // The click that captures the mouse is not a shot.
      if (this.enabled && e.target === this.canvas && e.button === 0)
        this.requestLock();
      return;
    }
    if (!this.enabled || this.hooks.chatIsOpen()) return;
    this.syncKeymap();
    const binding = mouseBinding(e.button);
    const actions = this.keymap.actions(binding);
    if (actions.length === 0) return;
    e.preventDefault();
    this.press(binding, actions, true);
  };

  private readonly onMouseUp = (e: MouseEvent): void => {
    const binding = mouseBinding(e.button);
    if (this.keys.down.has(binding)) this.release(binding);
  };

  private readonly onWheel = (e: WheelEvent): void => {
    if (!this.lockedState || !this.enabled || this.hooks.chatIsOpen()) return;
    if (settings.value.controls.jumpOnWheel) {
      // The wheel is the jump button: wheel bindings (weapon switching) do not fire at all.
      if (e.deltaY === 0) return;
      e.preventDefault();
      if (this.jumpNotches.push(e.deltaY, e.deltaMode, e.timeStamp))
        this.wheelJump.notch();
      return;
    }
    const binding = wheelBinding(e.deltaY);
    if (!binding) return;
    this.syncKeymap();
    const actions = this.keymap.actions(binding);
    if (actions.length === 0) return;
    e.preventDefault();
    if (this.wheel.push(e.deltaY, e.deltaMode, e.timeStamp))
      this.press(binding, actions, false);
  };

  private readonly onContextMenu = (e: Event): void => {
    if (this.lockedState || e.target === this.canvas) e.preventDefault();
  };

  private readonly onBlur = (): void => {
    if (document.visibilityState === "hidden" || !document.hasFocus())
      this.releaseAll();
  };

  // ---- Press / release ----

  private press(
    binding: string,
    actions: readonly Action[],
    physical: boolean,
  ): void {
    this.keys.press(binding, physical);
    const ctl = settings.value.controls;
    for (const action of actions) {
      switch (action) {
        case "fire":
          // Flush pending mouse movement so the recorded angles are the ones under the crosshair now.
          this.flushMouse();
          this.fires.push(performance.now(), this.yaw, this.pitch);
          this.hooks.onFire();
          break;
        case "ads":
          if (ctl.toggleAds) this.adsToggle = !this.adsToggle;
          break;
        case "crouch":
          if (ctl.toggleCrouch) this.crouchToggle = !this.crouchToggle;
          break;
        default:
          this.edge(action as EdgeAction);
      }
    }
  }

  private release(binding: string): void {
    this.keys.release(binding);
    for (const action of this.keymap.actions(binding)) {
      if (action === "scoreboard") this.hooks.onScoreboard(false);
    }
  }

  private edge(action: EdgeAction): void {
    switch (action) {
      case "primary":
        this.slots.select(0);
        break;
      case "secondary":
        this.slots.select(1);
        break;
      case "knife":
        this.slots.select(2);
        break;
      case "nextWeapon":
        this.slots.next();
        break;
      case "prevWeapon":
        this.slots.prev();
        break;
      case "lastWeapon":
        this.slots.last();
        break;
      case "scoreboard":
        this.hooks.onScoreboard(true);
        break;
      case "chat":
        this.releaseAll();
        this.hooks.onChat(false);
        break;
      case "teamChat":
        this.releaseAll();
        this.hooks.onChat(true);
        break;
      case "lobby":
        this.releaseAll();
        this.hooks.onLobby();
        break;
    }
  }

  private syncKeymap(): void {
    const kb = settings.value.keybinds;
    if (kb !== this.keybindsRef) {
      this.keybindsRef = kb;
      this.keymap = new Keymap(kb);
    }
  }

  /** Forget every held key and pending click (focus loss, lock loss, chat). */
  releaseAll(): void {
    const wasScoreboard = this.keys.down.size > 0;
    this.keys.clear();
    this.fires.clear();
    this.wheelJump.reset();
    this.pendingDx = 0;
    this.pendingDy = 0;
    if (wasScoreboard) this.hooks.onScoreboard(false);
  }

  // ---- Per frame ----

  /**
   * Turns the accumulated mouse deltas into view angles. `adsFrac`, `adsFovMult` (the weapon's
   * zoom at full ADS) and `scoped` select the sensitivity (`degreesPerCount`). Fills
   * `deltaPitchUpDeg`/`deltaYawRightDeg`.
   */
  applyMouse(adsFrac: number, adsFovMult: number, scoped: boolean): void {
    this.adsFrac = adsFrac;
    this.adsFovMult = adsFovMult;
    this.scoped = scoped;
    this.flushMouse();
  }

  private flushMouse(): void {
    if (this.pendingDx === 0 && this.pendingDy === 0) {
      return;
    }
    const ctl = settings.value.controls;
    const deg = degreesPerCount(
      ctl,
      this.adsFrac,
      this.adsFovMult,
      this.scoped,
    );
    const right = this.pendingDx * deg;
    const up = -this.pendingDy * deg * (ctl.invertY ? -1 : 1);
    this.pendingDx = 0;
    this.pendingDy = 0;
    this.deltaYawRightDeg += right;
    this.deltaPitchUpDeg += up;
    this.yaw = wrapYaw(this.yaw - right * DEG2RAD);
    this.pitch = clampPitch(this.pitch + up * DEG2RAD);
  }

  /** Clears the per-frame mouse delta accumulators after the frame consumed them. */
  endFrame(): void {
    this.deltaPitchUpDeg = 0;
    this.deltaYawRightDeg = 0;
  }

  /** Throws away mouse movement that arrived while the player cannot look (dead, paused). */
  discardMouse(): void {
    this.pendingDx = 0;
    this.pendingDy = 0;
  }

  /** Adds a view change that is not mouse input (recoil), in degrees (up / right positive). */
  addView(pitchUpDeg: number, yawRightDeg: number): void {
    this.yaw = wrapYaw(this.yaw - yawRightDeg * DEG2RAD);
    this.pitch = clampPitch(this.pitch + pitchUpDeg * DEG2RAD);
  }

  /** New life: face `yaw`, level pitch, drop toggles, request the primary weapon. */
  resetForSpawn(yaw: number): void {
    this.yaw = wrapYaw(yaw);
    this.pitch = 0;
    this.adsToggle = false;
    this.crouchToggle = false;
    this.prevButtons = 0;
    this.slots.reset(0);
    this.fires.clear();
    this.wheelJump.reset();
    this.pendingDx = 0;
    this.pendingDy = 0;
  }

  /**
   * Builds the command for `tick` into `out` (reused objects, no allocation unless the trigger
   * went down). `tickStartMs`/`tickDurMs` place a pending click inside the tick.
   */
  sample(
    tick: number,
    viewOffset: number,
    tickStartMs: number,
    tickDurMs: number,
    out: InputCmdData,
  ): InputCmdData {
    const live = this.enabled && this.lockedState && !this.hooks.chatIsOpen();
    let buttons = 0;
    out.fire = undefined;
    if (live) {
      this.syncKeymap();
      const ctl = settings.value.controls;
      this.overrides.ads = ctl.toggleAds ? this.adsToggle : undefined;
      this.overrides.crouch = ctl.toggleCrouch ? this.crouchToggle : undefined;
      buttons = buttonMask(
        this.keys.held,
        settings.value.keybinds,
        this.overrides,
      );
      // A wheel notch adds one jump tick (never clears a held jump); turning the setting off drops pending ones.
      if (ctl.jumpOnWheel) buttons = this.wheelJump.apply(buttons);
      else this.wheelJump.reset();
      buttons = resolveFire(
        this.fires,
        buttons,
        this.prevButtons,
        tickStartMs,
        tickDurMs,
        out,
      );
    } else {
      this.fires.clear();
      this.wheelJump.reset();
    }
    this.keys.endSample();
    this.prevButtons = buttons;
    out.tick = tick;
    out.buttons = buttons;
    out.yaw = quantizeYaw(this.yaw);
    out.pitch = quantizePitch(this.pitch);
    out.weaponSlot = this.slots.requested;
    out.viewOffset = viewOffset;
    return out;
  }
}
